// src/utils/myBandsCache.js

import { api } from "../services/api";

/**
 * /auth/me/bands -vastauksen kevyt käyttäjäkohtainen muistivälimuisti.
 *
 * Tavoitteet:
 * - Paavalikko ja Dashboard eivät tee samaa HTTP-pyyntöä yhtä aikaa.
 * - Dashboard voi käyttää jo ladattuja profiileja heti.
 * - Tuore data haetaan silti säännöllisesti uudelleen.
 * - Eri käyttäjien data EI koskaan käytä samaa cachea.
 */

const CACHE_TTL_MS = 30_000;
const cacheByUser = new Map();
const inFlightByUser = new Map();
const generationByUser = new Map();
let globalGeneration = 0;

function getUserGeneration(cacheKey) {
  return Number(generationByUser.get(cacheKey) || 0);
}

export function getMyBandsUserKey(user) {
  if (!user || typeof user !== "object") return null;

  const id =
    user.id ??
    user.user_id ??
    user._id ??
    user.account?.id ??
    user.account?.user_id ??
    null;

  if (id != null && String(id).trim()) {
    return `user:${String(id).trim()}`;
  }

  const email = String(user.email || "").trim().toLowerCase();
  return email ? `email:${email}` : null;
}

export function normalizeMyBandsResponse(res) {
  const list =
    Array.isArray(res?.bands)
      ? res.bands
      : Array.isArray(res?.items)
        ? res.items
        : Array.isArray(res?.memberships)
          ? res.memberships
          : Array.isArray(res)
            ? res
            : [];

  const items = list.map((membership) => {
    const profile = membership?.band || membership;
    const id = profile?.band_id ?? profile?.id;

    return {
      ...membership,
      ...profile,
      id,
      band_id: id,
      name:
        profile?.name ??
        profile?.band_name ??
        membership?.name ??
        membership?.band_name ??
        `#${id}`,
      role: membership?.role ?? profile?.role ?? "member",
    };
  });

  const meta = res?.meta || {};
  const maxBands = Number(meta.max_bands ?? 1);
  const ownerCount = Number(
    meta.bands_count ??
      items.filter(
        (item) => String(item?.role || "").toLowerCase() === "owner"
      ).length
  );

  return {
    items,
    meta,
    limits: {
      max_bands: maxBands,
      bands_count: ownerCount,
      can_add_band: ownerCount < maxBands,
    },
  };
}

function getCacheEntry(cacheKey) {
  if (!cacheKey) return null;
  return cacheByUser.get(cacheKey) ?? null;
}

export function peekMyBands({ allowStale = false, cacheKey = null } = {}) {
  if (!cacheKey) return null;

  const entry = getCacheEntry(cacheKey);
  if (!entry?.response) return null;
  if (allowStale) return entry.response;

  const age = Date.now() - Number(entry.cachedAt || 0);
  return age > CACHE_TTL_MS ? null : entry.response;
}

export async function getMyBands({ force = false, cacheKey = null } = {}) {
  if (!cacheKey) {
    return api.auth.meBands();
  }

  if (!force) {
    const cached = peekMyBands({ cacheKey });
    if (cached) return cached;
  }

  const existingRequest = inFlightByUser.get(cacheKey);
  if (existingRequest) return existingRequest;

  // Snapshot estää vanhan käyttäjän jo käynnissä olleen pyynnön kirjoittamasta
  // vastaustaan takaisin cacheen logoutin/käyttäjänvaihdon jälkeen.
  const requestGlobalGeneration = globalGeneration;
  const requestUserGeneration = getUserGeneration(cacheKey);

  const request = Promise.resolve(api.auth.meBands())
    .then((res) => {
      const generationStillMatches =
        globalGeneration === requestGlobalGeneration &&
        getUserGeneration(cacheKey) === requestUserGeneration;

      if (generationStillMatches) {
        cacheByUser.set(cacheKey, {
          response: res,
          cachedAt: Date.now(),
        });
      }

      return res;
    })
    .finally(() => {
      if (inFlightByUser.get(cacheKey) === request) {
        inFlightByUser.delete(cacheKey);
      }
    });

  inFlightByUser.set(cacheKey, request);
  return request;
}

export function invalidateMyBandsCache(cacheKey) {
  if (!cacheKey) return;
  cacheByUser.delete(cacheKey);
  inFlightByUser.delete(cacheKey);
  generationByUser.set(cacheKey, getUserGeneration(cacheKey) + 1);
}

export function clearMyBandsCache() {
  globalGeneration += 1;
  cacheByUser.clear();
  inFlightByUser.clear();
  generationByUser.clear();
}
